import {useLocalObservable} from 'mobx-react-lite';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {ECancelSubmitButtonRow, EForm} from 'webapp-lib/Components';

import {Dialog} from '#';
import {InfoText} from '#/InfoText';
import {useDialogController} from '_/hooks';
import {useTablePersistence} from '_/hooks/useTablePersistence';
import {casusStateItems} from '_/utilities/casus.utils';
import {requiredRule, useForm} from '_/utilities/form';
import {EFCheckboxGroup} from '_/utilities/form/controlled-components';

import {CasusSearchFilters} from './CasusAutocomplete.store';
import {
  CasusSearchFilterColumn,
  CasusSearchFilterRow,
} from './CasusAutocomplete.styles';
import {CasusSearchFilter} from './CasusAutocomplete.types';

export const useCasusSearchFilters = ({
  persistenceKey,
  showCasusFilteredOutInfo = false,
}: {
  persistenceKey: string;
  showCasusFilteredOutInfo?: boolean;
}): {
  persistenceInitialized: boolean;
  filtersModified: boolean;
  searchFilters: CasusSearchFilter;
  searchFilterText: string;
  showSearchFiltersDialog: () => void;
  searchFiltersDialog: React.ReactElement | null;
} => {
  const {isVisible, showDialog, closeDialog} = useDialogController();
  const searchFilters = useLocalObservable(() => new CasusSearchFilters());

  const persistenceInitialized = useTablePersistence({
    key: `casusAutocomplete.searchFilters.${persistenceKey}`,
    filters: searchFilters.filters,
  });

  const searchFiltersDialog = isVisible ? (
    <SearchFiltersDialog
      filters={searchFilters}
      showCasusFilteredOutInfo={showCasusFilteredOutInfo}
      onClose={closeDialog}
    />
  ) : null;

  return {
    persistenceInitialized,
    filtersModified: searchFilters.modified,
    searchFilters: searchFilters.casusSearchFilters,
    searchFilterText: searchFilters.searchFilterText,
    showSearchFiltersDialog: showDialog,
    searchFiltersDialog,
  };
};

enum FilterDialogCasusListFormField {
  CasusState = 'casusState',
}

const SearchFiltersDialog = ({
  filters,
  onClose,
  showCasusFilteredOutInfo,
}: {
  filters: CasusSearchFilters;
  onClose: () => void;
  showCasusFilteredOutInfo: boolean;
}): React.ReactElement => {
  const {t} = useTranslation('common');

  const {control, handleSubmit, formState} = useForm({
    defaultValues: {
      [FilterDialogCasusListFormField.CasusState]: filters.stateFilter.item,
    },
    onSubmit: ({casusState}) => {
      filters.stateFilter.setElements(casusState);
      onClose();
    },
  });

  const dialogContent = (
    <EForm onSubmit={handleSubmit}>
      <CasusSearchFilterRow>
        <CasusSearchFilterColumn>
          <EFCheckboxGroup
            control={control}
            items={casusStateItems}
            label={t('casusAutocomplete.searchFilters.dialog.casusStateFilter')}
            name={FilterDialogCasusListFormField.CasusState}
            rules={requiredRule}
          />
        </CasusSearchFilterColumn>
      </CasusSearchFilterRow>
      {showCasusFilteredOutInfo ? (
        <InfoText
          text={t(
            'casusAutocomplete.searchFilters.dialog.selectedCasusFilteredOutInfo'
          )}
        />
      ) : null}
      <ECancelSubmitButtonRow
        isSubmitting={formState.isSubmitting}
        submitButtonText={t('actions.apply')}
        onCancel={onClose}
      />
    </EForm>
  );

  return (
    <Dialog
      headerText={t('casusAutocomplete.searchFilters.dialog.header')}
      size='tiny'
      isShown
      onClose={onClose}
    >
      {dialogContent}
    </Dialog>
  );
};
